/**
 * FASE 4.2 — AreaClienteDrawer
 * Pannello laterale che ospita le sezioni Area Cliente (dashboard, polizze,
 * preventivi, documenti, profilo, notifiche, impostazioni) come overlay
 * sulla pagina pubblica corrente — NO navigazione.
 *
 * Riusa l'API /api/client/* esistente. Le viste embedded sono semplificate
 * per il drawer; per la versione full-screen restano i componenti originali
 * (importati on-demand quando serve un'azione complessa).
 */
import { useEffect, useState } from "react";
import { X, FileText, Briefcase, FolderOpen, User, Bell, Settings, LayoutDashboard, LogOut, ExternalLink, Users, Plus, Trash2, ShieldCheck, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import PrivacyDashboard from "@/components/auth/PrivacyDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;
// FASE K3 UX: endpoint familiari (namespace /api/clienti/*)
const API_FAM = `${process.env.REACT_APP_BACKEND_URL}/api/clienti`;

const SECTIONS = {
  dashboard: { title: "Dashboard", icon: LayoutDashboard },
  polizze: { title: "Le mie polizze", icon: FileText },
  preventivi: { title: "I miei preventivi", icon: Briefcase },
  documenti: { title: "I miei documenti", icon: FolderOpen },
  familiari: { title: "I miei familiari", icon: Users },  // FASE K3 UX
  profilo: { title: "Profilo", icon: User },
  privacy: { title: "Privacy & GDPR", icon: ShieldCheck },  // v1.67 FASE 3
  notifiche: { title: "Notifiche", icon: Bell },
  impostazioni: { title: "Impostazioni", icon: Settings },
};

export default function AreaClienteDrawer({ section, onClose }) {
  const { account, getToken, logout } = useClientAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!section || !account) return;
    // v1.67 FASE 3 — sezione privacy usa componente self-contained
    if (section === "privacy") { setLoading(false); setData({}); return; }
    let cancel = false;
    setLoading(true); setData(null);
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    const fetchers = {
      dashboard: () => Promise.all([
        axios.get(`${API}/policies`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/quote-requests`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/documents`, { headers }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: { items: [] } })),
      ]).then(([p, q, d, n]) => ({
        policies: p.data?.items || [],
        quotes: q.data?.items || [],
        documents: d.data?.items || [],
        notifications: n.data?.items || [],
      })),
      polizze: () => axios.get(`${API}/policies`, { headers }).then((r) => r.data),
      preventivi: () => axios.get(`${API}/quote-requests`, { headers }).then((r) => r.data),
      documenti: () => axios.get(`${API}/documents`, { headers }).then((r) => r.data),
      notifiche: () => axios.get(`${API}/notifications`, { headers }).then((r) => r.data),
      profilo: () => axios.get(`${API}/me`, { headers }).then((r) => r.data),
      impostazioni: () => axios.get(`${API}/me`, { headers }).then((r) => r.data),
      // FASE K3 UX: fetch familiari via /api/clienti/familiari
      familiari: () => axios.get(`${API_FAM}/familiari`, { headers }).then((r) => r.data),
    };
    (fetchers[section] || (() => Promise.resolve({})))()
      .then((r) => { if (!cancel) setData(r); })
      .catch(() => { if (!cancel) setData({ error: true }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [section, account, getToken]);

  if (!section) return null;
  const SectInfo = SECTIONS[section] || SECTIONS.dashboard;
  const Icon = SectInfo.icon;

  const onLogout = async () => {
    await logout();
    toast.success("Disconnesso");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose} data-testid="area-cliente-drawer-overlay">
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid={`area-cliente-drawer-${section}`}>
        <header className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#163321]">{SectInfo.title}</h2>
              <p className="text-[11px] text-zinc-500">{account?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-zinc-100" data-testid="drawer-close">
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-zinc-500 text-center py-12">Caricamento...</p>
          ) : section === "dashboard" ? (
            <DashboardView data={data} />
          ) : section === "polizze" ? (
            <ListView items={data?.items || []} empty="Nessuna polizza attiva"
              render={(p) => (
                <>
                  <p className="font-medium text-sm text-zinc-900">{p.numero_polizza || p.id}</p>
                  <p className="text-xs text-zinc-500">{p.ramo} · {p.compagnia || p.company_id || "—"}</p>
                  {p.data_scadenza && <p className="text-[11px] text-amber-700">Scade il {p.data_scadenza}</p>}
                </>
              )} />
          ) : section === "preventivi" ? (
            <ListView items={data?.items || []} empty="Nessun preventivo in corso"
              render={(q) => (
                <>
                  <p className="font-medium text-sm text-zinc-900">{q.tipo_richiesta || q.tipologia || "Preventivo"}</p>
                  <p className="text-xs text-zinc-500">Stato: {q.stato_pipeline || "nuovo"}</p>
                  <p className="text-[11px] text-zinc-400">{q.created_at?.slice(0, 10)}</p>
                </>
              )} />
          ) : section === "documenti" ? (
            <ListView items={data?.items || []} empty="Nessun documento caricato"
              render={(d) => (
                <>
                  <p className="font-medium text-sm text-zinc-900">{d.nome_file || d.title || d.id}</p>
                  <p className="text-xs text-zinc-500">{d.tipologia || d.kind || "documento"}</p>
                </>
              )} />
          ) : section === "notifiche" ? (
            <ListView items={data?.items || []} empty="Nessuna notifica"
              render={(n) => (
                <>
                  <p className={`text-sm ${n.read_at ? "text-zinc-500" : "font-medium text-zinc-900"}`}>{n.titolo || n.title}</p>
                  <p className="text-xs text-zinc-500">{n.body || n.messaggio}</p>
                </>
              )} />
          ) : section === "profilo" || section === "impostazioni" ? (
            <ProfileView data={data} onLogout={onLogout} />
          ) : section === "familiari" ? (
            <FamiliariView
              initialItems={data?.items || []}
              getToken={getToken}
            />
          ) : section === "privacy" ? (
            <PrivacyDashboard getToken={getToken} onLogout={onLogout} />
          ) : (
            <p className="text-sm text-zinc-500">Sezione in arrivo.</p>
          )}
        </div>

        <footer className="border-t border-zinc-100 px-5 py-3 flex items-center justify-between">
          <p className="text-[11px] text-zinc-400">PWA Area Cliente v1.7</p>
          <button onClick={onLogout} className="text-xs text-rose-600 hover:underline flex items-center gap-1"
            data-testid="drawer-logout">
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </footer>
      </aside>
    </div>
  );
}

function DashboardView({ data }) {
  if (!data) return null;
  const card = (label, count, icon, hint) => (
    <div className="bg-zinc-50 rounded-xl p-3 border border-zinc-100">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-2xl font-semibold text-[#163321]">{count}</p>
      {hint && <p className="text-[11px] text-zinc-500 mt-0.5">{hint}</p>}
    </div>
  );
  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500">Riepilogo Area Cliente — tutto qui senza cambiare pagina.</p>
      <div className="grid grid-cols-2 gap-3">
        {card("Polizze", data.policies?.length || 0, "📋", "attive")}
        {card("Preventivi", data.quotes?.length || 0, "💼", "totali")}
        {card("Documenti", data.documents?.length || 0, "📄", "caricati")}
        {card("Notifiche", data.notifications?.filter(n => !n.read_at).length || 0, "🔔", "non lette")}
      </div>
      {/* FASE K3 UX — shortcut ai familiari */}
      <a
        href="/area-cliente/familiari"
        data-testid="dashboard-link-familiari"
        className="block rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 p-3 transition"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#163321]">I miei familiari</p>
            <p className="text-[11px] text-emerald-800/70 mt-0.5">
              Aggiungi/gestisci familiari per preventivi facilitati
            </p>
          </div>
          <span className="text-2xl">👨‍👩‍👧</span>
        </div>
      </a>
      {/* v1.67 FASE 3 — shortcut a privacy dashboard */}
      <a
        href="/?drawer=privacy"
        data-testid="dashboard-link-privacy"
        className="block rounded-xl bg-white hover:bg-zinc-50 border border-zinc-200 p-3 transition"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#163321]">Privacy & GDPR</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Gestisci consensi, dati e diritti GDPR
            </p>
          </div>
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
        </div>
      </a>
    </div>
  );
}

function ListView({ items, render, empty }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-zinc-400 text-center py-12">{empty}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={it.id || i} className="bg-zinc-50 hover:bg-emerald-50/40 rounded-xl p-3 border border-zinc-100 cursor-pointer transition">
          {render(it)}
        </li>
      ))}
    </ul>
  );
}

/* ==== FASE K3 UX — I miei familiari ==== */
function FamiliariView({ initialItems, getToken }) {
  const [items, setItems] = useState(initialItems || []);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: "", cognome: "", codice_fiscale: "",
    grado_parentela: "coniuge", data_nascita: "",
    telefono: "", email: "",
  });

  useEffect(() => setItems(initialItems || []), [initialItems]);

  const refresh = async () => {
    try {
      const t = getToken();
      const r = await axios.get(`${API_FAM}/familiari`,
        { headers: { Authorization: `Bearer ${t}` } });
      setItems(r.data?.items || []);
    } catch { /* ignore */ }
  };

  const addFamiliare = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || form.codice_fiscale.length !== 16) {
      toast.error("Compila nome, cognome e codice fiscale (16 caratteri)");
      return;
    }
    setSaving(true);
    try {
      const t = getToken();
      await axios.post(`${API_FAM}/familiari`, form,
        { headers: { Authorization: `Bearer ${t}` } });
      toast.success("Familiare aggiunto");
      setShowAdd(false);
      setForm({ nome: "", cognome: "", codice_fiscale: "",
        grado_parentela: "coniuge", data_nascita: "", telefono: "", email: "" });
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const removeFamiliare = async (fid) => {
    if (!window.confirm("Rimuovere questo familiare?")) return;
    try {
      const t = getToken();
      await axios.delete(`${API_FAM}/familiari/${fid}`,
        { headers: { Authorization: `Bearer ${t}` } });
      toast.success("Familiare rimosso");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rimozione");
    }
  };

  return (
    <div className="space-y-3" data-testid="familiari-view">
      <p className="text-xs text-zinc-500">
        Aggiungi i familiari per cui potresti richiedere polizze. Dopo la
        verifica dei documenti da parte di un operatore, i loro dati saranno
        pre-compilati nei nuovi wizard.
      </p>
      {!showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="familiari-btn-add-open"
          className="w-full inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 rounded-full bg-[#163321] text-white text-sm font-semibold hover:bg-[#0e1f15]"
        >
          <Plus className="w-4 h-4" /> Aggiungi familiare
        </button>
      )}

      {showAdd && (
        <form
          onSubmit={addFamiliare}
          data-testid="familiari-form-add"
          className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-4 space-y-2.5"
        >
          <h4 className="text-sm font-semibold text-[#163321]">Nuovo familiare</h4>
          <div className="grid grid-cols-2 gap-2">
            <input
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
              placeholder="Nome *"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              data-testid="familiari-input-nome"
            />
            <input
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
              placeholder="Cognome *"
              value={form.cognome}
              onChange={(e) => setForm({ ...form, cognome: e.target.value })}
              data-testid="familiari-input-cognome"
            />
          </div>
          <input
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm uppercase"
            placeholder="Codice fiscale (16 caratteri) *"
            maxLength={16}
            value={form.codice_fiscale}
            onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
            data-testid="familiari-input-cf"
          />
          <select
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
            value={form.grado_parentela}
            onChange={(e) => setForm({ ...form, grado_parentela: e.target.value })}
            data-testid="familiari-input-parentela"
          >
            <option value="coniuge">Coniuge</option>
            <option value="figlio">Figlio/a</option>
            <option value="genitore">Genitore</option>
            <option value="altro">Altro</option>
          </select>
          <input
            type="date"
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
            placeholder="Data di nascita"
            value={form.data_nascita}
            onChange={(e) => setForm({ ...form, data_nascita: e.target.value })}
            data-testid="familiari-input-datanascita"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
              placeholder="Telefono"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              data-testid="familiari-input-telefono"
            />
            <input
              type="email"
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="familiari-input-email"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
              data-testid="familiari-btn-cancel"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-3 py-2 rounded-lg bg-[#163321] text-white text-sm font-semibold hover:bg-[#0e1f15] disabled:opacity-50"
              data-testid="familiari-btn-save"
            >
              {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 leading-snug">
            NB: dopo l&apos;inserimento, un operatore Colucci verificherà l&apos;identità
            del familiare. Potrai caricare i documenti (identità + eventuale
            busta paga) nella pagina di dettaglio.
          </p>
        </form>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-zinc-400 text-center py-8" data-testid="familiari-empty">
          Nessun familiare registrato.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="familiari-list">
          {items.map((f) => {
            const idVerified = f.documenti?.identita?.verified;
            return (
              <li
                key={f.id}
                className="bg-white border border-zinc-200 rounded-xl p-3 flex items-start justify-between gap-3"
                data-testid={`familiari-item-${f.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-zinc-900">
                    {f.nome} {f.cognome}
                  </p>
                  <p className="text-xs text-zinc-500 capitalize">
                    {f.grado_parentela} · CF {f.codice_fiscale}
                  </p>
                  <div className="mt-1.5">
                    {idVerified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-medium">
                        <ShieldCheck className="w-3 h-3" /> Verificato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium">
                        <Clock className="w-3 h-3" /> In attesa di verifica
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFamiliare(f.id)}
                  className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500"
                  data-testid={`familiari-btn-delete-${f.id}`}
                  aria-label="Rimuovi"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ProfileView({ data, onLogout }) {
  const acc = data?.account;
  const profiles = data?.profiles || [];
  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wider text-emerald-700 font-medium">Account</p>
        <p className="text-base font-medium text-zinc-900 mt-1">{acc?.email}</p>
        {acc?.phone_e164 && <p className="text-xs text-zinc-500">{acc.phone_e164}</p>}
        {acc?.linked_customer_id && <p className="text-[11px] text-emerald-700 mt-1">✓ Linkato al CRM</p>}
      </div>
      {profiles.map((p) => (
        <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{p.ruolo || p.tipo_profilo || "profilo"}</p>
          <p className="text-sm font-medium text-zinc-900 mt-1">
            {p.anagrafica?.nome} {p.anagrafica?.cognome}
          </p>
          {p.residenza?.citta && (
            <p className="text-xs text-zinc-500">{p.residenza.via}, {p.residenza.citta} ({p.residenza.provincia})</p>
          )}
        </div>
      ))}
      <a href="/area-cliente/dashboard" target="_blank"
        className="block text-xs text-emerald-700 hover:underline flex items-center gap-1">
        <ExternalLink className="w-3 h-3" /> Apri PWA in nuova scheda
      </a>
      <button onClick={onLogout}
        className="w-full mt-3 px-4 py-2 border border-rose-200 text-rose-600 rounded-lg hover:bg-rose-50 text-sm flex items-center justify-center gap-2"
        data-testid="profile-logout">
        <LogOut className="w-4 h-4" /> Disconnetti
      </button>
    </div>
  );
}
